import { Flex } from "@mantine/core";
import { type CSSProperties, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTheme } from "src/hooks";
import { ICol } from "src/interfaces";
import { useDispatch, useSelector } from "src/redux/hooks";
import { handleSpec } from "src/redux/slices/entriesGeneratorSlice";

import SortArrow from "../SortArrow/SortArrow";

interface Props {
  col: ICol;
  rowspan?: number;
  colspan?: number;
  sIndex: number;
  cIndex?: number;
  style?: CSSProperties;
  contentWrapperStyles?: any;
  hasChildren?: boolean;
  thStyling?: boolean;
  pdfEntry?: boolean;
  hideHeaders?: boolean;
  useLayout?: boolean;
}

const HeaderCell = ({
  col,
  rowspan = 1,
  colspan = 1,
  sIndex,
  style,
  hasChildren,
  contentWrapperStyles,
  thStyling,
  pdfEntry,
  hideHeaders,
  useLayout,
}: Props) => {
  const dispatch = useDispatch();
  const theme = useTheme() as any;
  const selectingMode = useSelector(
    state => state.entriesGeneratorSlice.selectingMode,
  );
  const selectedCols = useSelector(
    state => state.entriesGeneratorSlice.selectedCols,
  );
  const selectedLogbook = useSelector(
    state => state.entriesGeneratorSlice.selectedLogbook,
  );
  const type = useSelector(
    state => state.entriesGeneratorSlice.reportDetails?.type,
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const handleClick = () => {
    if (col.sortByName) {
      const currentSort = searchParams.get("sort");
      const currentDir = searchParams.get("dir");

      const newDir =
        currentSort === col.sortByName && currentDir === "asc" ? "desc" : "asc";

      setSearchParams({ sort: col.sortByName, dir: newDir });
    }

    if (selectingMode && !hasChildren && col.name && col.chartable !== false) {
      dispatch(handleSpec({ section: sIndex, col: col.name }));
    }
  };

  const isChartSelectable = !hasChildren && col.chartable !== false;

  const isSelected =
    col.name && selectedCols[selectedLogbook]?.[sIndex]
      ? selectedCols[selectedLogbook][sIndex].includes(col.name)
      : false;
  const headerName = useMemo(
    () => col.name?.replaceAll("<br>", " "),
    [col.name],
  );
  if (col.unit === "space" || col.type === "space")
    if (col.unit === "space" || col.type === "space") {
      return (
        <th
          className="border-none"
          style={{
            backgroundColor: theme.colors.background[0],
            minWidth: 15,
            border: "none",
          }}
        />
      );
    }
  return (
    <th
      style={{
        ...(hideHeaders && { opacity: 0 }),
        ...(!hideHeaders && {
          border: `1px solid ${theme.colors.thBorder[0]}`,
        }),
        borderBottom: "none",
        ...(!hideHeaders && { padding: type === 3 ? 0 : pdfEntry ? 4 : 8 }),
        fontSize: pdfEntry ? 10 : "0.875rem",
        ...(selectingMode &&
          isChartSelectable && { cursor: "pointer", userSelect: "none" }),
        ...(selectingMode && !isChartSelectable && { cursor: "default" }),
        ...(selectingMode &&
          isSelected && {
            backgroundColor: theme.colors.selectedHeaderCell[0],
          }),
        ...(!useLayout && col.width && { width: Number(col.width) * 10 }),
        ...(thStyling && {
          backgroundColor: theme.colors.thBackgroundColor[0],
          color: theme.colors.thColor[0],
        }),
        ...style,
        ...(col.hidden && {
          visibility: "hidden",
          border: "none",
          pointerEvents: "none",
          backgroundColor: "transparent",
          height: "0px",
          padding: 0,
          margin: 0,
        }),
      }}
      colSpan={colspan}
      rowSpan={rowspan}
      onClick={handleClick}>
      {!col.hidden && (
        <Flex
          align="center"
          justify={col.align ? col.align : "center"}
          gap={pdfEntry ? 4 : 8}
          style={{
            position: "relative",
            ...(hideHeaders && { height: 0, padding: 0, margin: 0 }),
          }}>
          <Flex
            gap={pdfEntry ? 2 : 5}
            wrap="wrap"
            align="center"
            style={contentWrapperStyles ?? {}}>
            <span style={{ margin: 0 }}>{headerName}</span>
            {col.displayValue && (
              <span
                style={{
                  margin: 0,
                  fontWeight: 200,
                  fontSize: pdfEntry ? 10 : "1.125em",
                }}>
                {col.displayValue}
              </span>
            )}
          </Flex>
          {col.sortByName && col.sortByName === searchParams.get("sort") && (
            <SortArrow />
          )}
        </Flex>
      )}
    </th>
  );
};

export default HeaderCell;
